import {
  Injectable,
  OnDestroy,
  Injector,
  EventEmitter,
  Input,
  Inject
} from '@angular/core';
import {
  Unboxed,
  KeyValue,
  FormArrayState,
  AbstractControlState,
  FormGroupState
} from 'ngrx-forms';
import {
  Observable,
  Subscription
} from 'rxjs';
import { AbstractFormDefinition } from './abstract-form-definition';
import { FormsSources } from './forms-sources';
import {
  Store,
  MemoizedSelector
} from '@ngrx/store';
import { SetInputMapEntry } from './decorators/controls/set-input';
import { OnEventMapEntry } from './decorators/controls/on-event';
import {
  ON_EVENT_USER_DEFINED_PROPERTY,
  SET_INPUT_USER_DEFINED_PROPERTY
} from './decorators/controls/user-defined-property-keys';
import { DxFormArray } from './form-array';
import { NGXLogger } from 'ngx-logger';
import {
  map,
  filter,
  first,
  tap
} from 'rxjs/operators';
import { BASE_GROUP_STATE_SELECTOR } from './tokens';

export interface OnFormArraySet {
  dxOnFormArraySet(): void;
}

export const dxOnFormArraySet = 'dxOnFormArraySet';

@Injectable()
export class AbstractFormArrayComponent<
  FormValue extends KeyValue,
  FormArray extends DxFormArray<FormValue, any, any> = DxFormArray<
    FormValue,
    any,
    any
  >,
  FormDefinition extends AbstractFormDefinition<any> = AbstractFormDefinition<
    any
  >,
  Value extends Array<Unboxed<FormValue>> = Array<Unboxed<FormValue>>
> implements OnDestroy {
  public get group(): FormGroupState<any> {
    return this._group;
  }

  @Input() public set group(value: FormGroupState<any>) {
    this._group = value;
    if (!this.ngrxFormArray) {
      if (this.controlPath) {
        this.ngrxFormArray = this.applyControlPath(this._group, this.controlPath)
      }
    }
  }

  private _group: FormGroupState<any>;
  private _controlPath: string;

  public get controlPath(): string {
    return this._controlPath;
  }

  @Input() public set controlPath(controlPath: string) {
    this._controlPath = controlPath;
    if (!this.ngrxFormArray) {
      if (this.group) {
        this.ngrxFormArray = this.applyControlPath(this.group, controlPath);
      } else  if (this.baseSelector) {
        this.store$.select(this.baseSelector)
            .pipe(
              filter(Boolean),
              first(),
              map(baseState => this.applyControlPath(baseState, controlPath)),
              tap(state => this.ngrxFormArray = state),
            ).subscribe()

      } else {
        this.logger.warn('[AbstractFormArrayComponent::(set)controlPath] baseSelector is not defined');
      }
    }
  }

  public get dxFormArray(): FormArray {
    return this._dxFormArray;
  }

  public set dxFormArray(value: FormArray) {
    this._dxFormArray = value;
    this.value$ = this.dxFormArray.__value$ as any;
    this.state$ = this.dxFormArray.__state$ as any;
    this.groups$ = this.state$.pipe(map(state => state.controls)) as any;
    if (
      this[dxOnFormArraySet] &&
      typeof this[dxOnFormArraySet] === 'function'
    ) {
      this[dxOnFormArraySet]();
    }
  }

  @Input()
  public set ngrxFormArray(value: FormArrayState<FormValue>) {
    this.state = value;
    const formId = value.id.match(/^[^\.]+/)[0];
    if (!this.formDefinition || this.formDefinition.formId !== formId) {
      const controlPath = value.id
        .match(/^[^\.]+\.(.*)$/)[1]
        .split('.')
        .join('/');
      this.formDefinition = this.formsSources.getForm(formId);
      this.dxFormArray = this.formDefinition.getControl(controlPath);
    }
    this.processUserDefinedProperties(value.userDefinedProperties);
  }

  public get valid(): boolean {
    return Object.keys(this.state.errors || {}).length === 0;
  }

  public get invalid(): boolean {
    return !this.valid;
  }

  public get value(): Value {
    return this._dxFormArray && (this._dxFormArray.__value as any);
  }

  public set value(value: Value) {
    if (this._dxFormArray) {
      this._dxFormArray.setValue(value);
    }
  }

  public value$: Observable<Value>;
  public state$: Observable<FormArrayState<FormValue>>;
  public groups$: Observable<ReadonlyArray<FormGroupState<FormValue>>>;

  private _dxFormArray: FormArray;
  public formDefinition: FormDefinition;
  public state: FormArrayState<FormValue>;
  protected _subscription = new Subscription();
  private _onEventSubscriptions: string[] = [];

  public readonly baseSelector: MemoizedSelector<any, FormGroupState<any>>;

  public constructor(
    public injector: Injector,
    public formsSources: FormsSources,
    public store$: Store<any>,
    public logger: NGXLogger,
    @Inject(BASE_GROUP_STATE_SELECTOR) baseSelector: any,
  ) {
    this.baseSelector = baseSelector;
  }

  private applyControlPath(
    baseState: FormGroupState<any>,
    controlPath: string
  ): FormArrayState<any> {
    this.logger.trace(
      `[AbstractFormArrayComponent::ngOnInit::${controlPath}] baseState`,
      baseState
    );
    const targetState: FormArrayState<any> = controlPath
      .split('/')
      .reduce((state, controlId) => {
        if (state.controls.hasOwnProperty(controlId)) {
          const newState = state.controls[controlId];
          this.logger.trace(
            `[AbstractFormArrayComponent::ngOnInit::${controlPath}] reduce state`,
            { controlId, newState }
          );
          return newState;
        }
        this.logger.error(
          `[AbstractFormArrayComponent::ngOnInit::${controlPath}] control path is not valid`,
          { dxFormControl: controlPath, baseState }
        );
        throw new Error(
          `[AbstractFormArrayComponent::ngOnInit::${controlPath}] control path is not valid`
        );
      }, baseState) as any;
    this.logger.trace(
      `[AbstractFormArrayComponent::ngOnInit::${controlPath}] targetState`,
      targetState
    );

    if (!targetState.controls || !Array.isArray(targetState.controls)) {
      throw new Error(
        `[AbstractFormArrayComponent::ngOnInit::${controlPath}] targetState is not an FormArrayState`
      );
    }

    return targetState as FormArrayState<any>;
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._subscription = new Subscription();
    this._onEventSubscriptions = [];
  }

  public trackBy(index: number, item: AbstractControlState<any>): string {
    return item.id + item.isValid;
  }

  public onDropFormGroup($event: {
    previousIndex: number;
    currentIndex: number;
  }) {
    this.dxFormArray.moveControl($event.previousIndex, $event.currentIndex);
  }

  public onAddFormGroup(initial?: Partial<Unboxed<FormValue>>): void {
    this.dxFormArray.addFormGroup(initial);
  }

  public onRemoveFormGroup(index: number): void {
    this.dxFormArray.removeFormGroup(index);
  }

  public processUserDefinedProperties(userDefinedProperties: KeyValue): void {
    if (userDefinedProperties.hasOwnProperty(ON_EVENT_USER_DEFINED_PROPERTY)) {
      for (const onEvent of userDefinedProperties[
        ON_EVENT_USER_DEFINED_PROPERTY
      ] as Array<OnEventMapEntry>) {
        if (
          this.hasOwnProperty(onEvent.output) &&
          this[onEvent.output] instanceof EventEmitter
        ) {
          if (
            !this._onEventSubscriptions.find(
              output => output === onEvent.output
            )
          ) {
            this._subscription.add(
              (<EventEmitter<any>>this[onEvent.output]).subscribe($event =>
                onEvent.handler($event)
              )
            );
            this._onEventSubscriptions.push(onEvent.output);
          }
        } else {
          console.warn(
            `Defined output '${onEvent.output}' is not found on control '${
              Object.getPrototypeOf(this).constructor.name
            }'`
          );
        }
      }
    }
    if (userDefinedProperties.hasOwnProperty(SET_INPUT_USER_DEFINED_PROPERTY)) {
      for (const setInput of userDefinedProperties[
        SET_INPUT_USER_DEFINED_PROPERTY
      ] as Array<SetInputMapEntry>) {
        if (this.hasOwnProperty(setInput.input)) {
          this[setInput.input] = setInput.value;
        } else {
          console.warn(
            `Defined input '${setInput.input}' is not found on control '${
              Object.getPrototypeOf(this).constructor.name
            }'`
          );
        }
      }
    }
  }
}
